/**
 * Client-side PDF export for a Field Plan job.
 * Produces a single-column A4 report: brand header, job details, utility
 * legend of what was found, pin/line tables, sketch strokes summary,
 * AI site report body, and (if present) client sign-off block.
 */
import jsPDF from "jspdf";
import * as htmlToImage from "html-to-image";
import { UTILITIES, getUtil } from "@/constants/utilities";
import { BRAESIDE_LOGO_URL } from "@/constants/brand";

const AU_DATE = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

async function loadImageDataUrl(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function hexToRgb(hex) {
  const m = hex.replace("#", "");
  const v = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
}

/**
 * Snapshot a live DOM node (Leaflet map, sketch canvas) as a PNG data URL.
 * Uses html-to-image which handles tile <img> elements + inline SVG better
 * than html2canvas. Returns null on failure so PDF export never blocks.
 */
export async function snapshotNode(selector) {
  if (typeof document === "undefined") return null;
  const el = document.querySelector(selector);
  if (!el || el.offsetWidth === 0 || el.offsetHeight === 0) return null;
  try {
    // JPEG @ 0.85 quality instead of PNG — cuts PDF size 5-10x with no
    // visible loss on satellite / sketch overlays. Prevents the emailed
    // PDF from breaching Resend's ~40 MB attachment cap.
    return await htmlToImage.toJpeg(el, {
      pixelRatio: 1.5,
      quality: 0.85,
      cacheBust: true,
      backgroundColor: "#ffffff",
      filter: (node) => {
        if (!(node instanceof HTMLElement)) return true;
        if (node.classList && node.classList.contains("leaflet-control-attribution")) return false;
        return true;
      },
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("PDF snapshot failed for", selector, err);
    return null;
  }
}

/**
 * Place a captured image as a huge full-page picture onto the PDF.
 * Adds the page as A4 LANDSCAPE with tight 12pt margins so the map/sketch
 * uses ~95% of the paper — biggest possible read for the client.
 * Preserves aspect ratio; portrait-shaped images centre horizontally.
 */
function drawFullImage(doc, dataUrl, aspect, _ctx) {
  const IMAGE_MARGIN = 12; // px — tight so plans fill the page
  const hasTitle = !!(_ctx.title && _ctx.title.trim());
  doc.addPage("a4", "landscape");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const titleH = hasTitle ? 16 : 0;
  const titleGap = hasTitle ? 8 : 0;
  const usableW = pageW - IMAGE_MARGIN * 2;
  const usableH = pageH - IMAGE_MARGIN * 2 - titleH - titleGap;

  // Fit rectangle preserving aspect ratio.
  let fittedW = usableW;
  let fittedH = usableW / aspect;
  if (fittedH > usableH) {
    fittedH = usableH;
    fittedW = usableH * aspect;
  }
  const offsetX = IMAGE_MARGIN + (usableW - fittedW) / 2;
  const offsetY = IMAGE_MARGIN + titleH + titleGap;

  if (hasTitle) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(_ctx.title, IMAGE_MARGIN, IMAGE_MARGIN + 10);
  }
  try {
    doc.addImage(dataUrl, "JPEG", offsetX, offsetY, fittedW, fittedH);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("addImage failed", err);
  }
}

export async function buildJobPdf(job, user, opts = {}) {
  const { planOnly = false, mapSnap: preMapSnap = null, sketchSnap: preSketchSnap = null,
          mapAspect: preMapAspect = null, sketchAspect: preSketchAspect = null } = opts;
  // Snapshot the live map + sketch BEFORE we build the PDF so the DOM is
  // still visible/rendered exactly as the tradie sees it onsite. Callers can
  // pre-supply snapshots (used by the Email Plan flow which toggles tabs to
  // ensure both trees are mounted at capture time).
  const mapSnap = preMapSnap ?? await snapshotNode("#fieldplan-map-capture");
  const mapEl = document.querySelector("#fieldplan-map-capture");
  const mapAspect = preMapAspect ?? (mapEl ? mapEl.offsetWidth / mapEl.offsetHeight : 4 / 3);
  const sketchSnap = preSketchSnap ?? await snapshotNode("#fieldplan-sketch-capture");
  const sketchEl = document.querySelector("#fieldplan-sketch-capture");
  const sketchAspect = preSketchAspect ?? (sketchEl ? sketchEl.offsetWidth / sketchEl.offsetHeight : 4 / 3);

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  const contentW = pageW - margin * 2;
  const drawCtx = { pageW, pageH, margin, contentW };
  let y = margin;

  const ensureSpace = (need) => {
    if (y + need > pageH - margin - 20) {
      doc.addPage();
      y = margin;
    }
  };

  // ---------- Header ----------
  // Skip the full text report (header, job details, tables, sign-off) when
  // `planOnly` is on — used by the email flow so recipients get just the
  // visual map + sketch pages with no cover.
  if (!planOnly) {
  const logo = await loadImageDataUrl(BRAESIDE_LOGO_URL);
  if (logo) {
    try { doc.addImage(logo, "PNG", margin, y, 48, 48); } catch (_) {}
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.text("BRAESIDE PLUMBING & DRAINAGE", margin + 58, y + 15);
  doc.setFontSize(20);
  doc.setTextColor(20);
  doc.text("FIELD PLAN — SITE REPORT", margin + 58, y + 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(`Generated ${AU_DATE()}`, margin + 58, y + 47);
  y += 66;
  doc.setDrawColor(0);
  doc.setLineWidth(1);
  doc.line(margin, y, pageW - margin, y);
  y += 14;

  // ---------- Job details ----------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.text(job.name || "Untitled site", margin, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(80);
  if (job.address) { doc.text(job.address, margin, y); y += 12; }
  const gps = `Site GPS: ${(job.center_lat ?? 0).toFixed(5)}, ${(job.center_lng ?? 0).toFixed(5)}`;
  doc.text(gps, margin, y); y += 12;
  const preparedBy = user?.full_name || user?.email || "";
  const trade = user?.trade ? ` (${user.trade})` : "";
  if (preparedBy) { doc.text(`Prepared by: ${preparedBy}${trade}`, margin, y); y += 12; }
  if (job.notes) {
    y += 4;
    doc.setTextColor(40);
    const lines = doc.splitTextToSize(`Notes: ${job.notes}`, contentW);
    doc.text(lines, margin, y);
    y += lines.length * 12 + 6;
  }
  y += 4;

  // ---------- Utility totals ----------
  const svcCount = {};
  (job.pins || []).forEach((p) => { svcCount[p.service] = (svcCount[p.service] || 0) + 1; });
  const lineCount = {};
  (job.lines || []).forEach((l) => { lineCount[l.service] = (lineCount[l.service] || 0) + 1; });
  const usedKeys = new Set([...Object.keys(svcCount), ...Object.keys(lineCount)]);

  if (usedKeys.size) {
    ensureSpace(80);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(0);
    doc.text("SERVICES RECORDED", margin, y); y += 14;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    const rowH = 16;
    const swatch = 10;
    UTILITIES.filter((u) => usedKeys.has(u.key)).forEach((u) => {
      ensureSpace(rowH);
      const [r, g, b] = hexToRgb(u.color);
      doc.setFillColor(r, g, b);
      doc.setDrawColor(0);
      doc.rect(margin, y - swatch + 2, swatch, swatch, "FD");
      doc.setTextColor(30);
      doc.text(u.label, margin + swatch + 6, y);
      const pinN = svcCount[u.key] || 0;
      const lineN = lineCount[u.key] || 0;
      doc.setTextColor(90);
      doc.text(`${pinN} pin${pinN !== 1 ? "s" : ""}  ·  ${lineN} line run${lineN !== 1 ? "s" : ""}`,
        margin + 130, y);
      y += rowH;
    });
    y += 6;
  }

  // ---------- Pin table ----------
  if ((job.pins || []).length) {
    ensureSpace(28);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(0);
    doc.text("LOCATED PINS", margin, y); y += 12;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(30);
    doc.setDrawColor(200);
    // Header row
    doc.text("Service", margin, y);
    doc.text("Lat, Lng", margin + 90, y);
    doc.text("Label", margin + 240, y);
    doc.text("Depth", pageW - margin - 40, y, { align: "right" });
    y += 4;
    doc.line(margin, y, pageW - margin, y);
    y += 10;
    job.pins.forEach((p) => {
      ensureSpace(14);
      const util = getUtil(p.service);
      const [r, g, b] = hexToRgb(util.color);
      doc.setFillColor(r, g, b);
      doc.rect(margin, y - 8, 6, 8, "F");
      doc.setTextColor(30);
      doc.text(util.label, margin + 12, y);
      doc.text(`${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`, margin + 90, y);
      const label = (p.label || "").slice(0, 40);
      if (label) doc.text(label, margin + 240, y);
      const depth = p.depth_mm != null ? `${p.depth_mm} mm` : "—";
      doc.text(depth, pageW - margin - 40, y, { align: "right" });
      y += 14;
    });
    y += 8;
  }

  // ---------- Line runs ----------
  if ((job.lines || []).length) {
    ensureSpace(28);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(0);
    doc.text("LINE RUNS RECORDED", margin, y); y += 12;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(30);
    job.lines.forEach((ln) => {
      ensureSpace(14);
      const util = getUtil(ln.service);
      const [r, g, b] = hexToRgb(util.color);
      doc.setFillColor(r, g, b);
      doc.rect(margin, y - 8, 6, 8, "F");
      const label = ln.label ? ` — ${ln.label}` : "";
      doc.setTextColor(30);
      doc.text(`${util.label}: ${ln.coords.length} point${ln.coords.length !== 1 ? "s" : ""}${label}`,
        margin + 12, y);
      y += 14;
    });
    y += 8;
  }

  // ---------- AI report ----------
  if ((job.ai_summary || "").trim()) {
    ensureSpace(30);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(0);
    doc.text("AI SITE REPORT", margin, y); y += 14;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(30);
    const wrapped = doc.splitTextToSize(job.ai_summary, contentW);
    wrapped.forEach((ln) => {
      ensureSpace(13);
      doc.text(ln, margin, y);
      y += 13;
    });
    y += 8;
  }

  // ---------- Sketch info ----------
  if ((job.strokes || []).length) {
    ensureSpace(18);
    doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(90);
    doc.text(`${job.strokes.length} sketch stroke${job.strokes.length !== 1 ? "s" : ""} on the onsite canvas (open the job in Field Plan to view).`,
      margin, y);
    y += 14;
    doc.setFont("helvetica", "normal");
  }

  // ---------- Client sign-off ----------
  if (job.client_signature || job.client_name) {
    ensureSpace(120);
    doc.setDrawColor(0);
    doc.line(margin, y, pageW - margin, y);
    y += 12;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(0);
    doc.text("CLIENT SIGN-OFF", margin, y); y += 14;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(30);
    doc.text(`Signed by: ${job.client_name || "—"}`, margin, y); y += 12;
    if (job.signed_at) { doc.text(`Date: ${AU_DATE(job.signed_at)}`, margin, y); y += 12; }
    if (job.client_signature) {
      const sigW = 220, sigH = 80;
      ensureSpace(sigH + 8);
      try { doc.addImage(job.client_signature, "PNG", margin, y, sigW, sigH); } catch (_) {}
      doc.setDrawColor(180);
      doc.rect(margin, y, sigW, sigH);
      y += sigH + 6;
    }
  }
  }  // end of !planOnly block

  // ---------- Visual plans (map + sketch snapshots) ----------
  // Full-page renders of the actual locating plan the tradie built onsite —
  // this is the "detailed locating plan" clients care about, not just the
  // table summary above.
  if (mapSnap) {
    drawFullImage(doc, mapSnap, mapAspect, {
      ...drawCtx,
      title: planOnly ? "" : "MAP PLAN — LOCATED SERVICES OVERLAY",
    });
  }
  if (sketchSnap) {
    drawFullImage(doc, sketchSnap, sketchAspect, {
      ...drawCtx,
      title: planOnly ? "" : "SKETCH PLAN — ONSITE ANNOTATIONS",
    });
  }
  // In plan-only mode, drop the initial blank page that jsPDF auto-creates
  // (we never wrote to it). Result: recipient opens straight into the plan.
  if (planOnly) {
    try { doc.deletePage(1); } catch (_) { /* if only 1 page exists we still want it */ }
  }
  // Reset y for anything appended below on the last (image) page.
  y = margin + 12;

  // ---------- Footer on every page ----------
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    // Use per-page dimensions so the footer sits inside the paper on both
    // portrait (report) and landscape (map/sketch) pages.
    const pW = doc.internal.pageSize.getWidth();
    const pH = doc.internal.pageSize.getHeight();
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(140);
    doc.text("Field Plan by Braeside Plumbing & Drainage",
      margin, pH - 14, { maxWidth: pW - margin * 2 });
    doc.text(`${i} / ${totalPages}`, pW - margin, pH - 14, { align: "right" });
  }

  const filename = `field-plan_${(job.name || "site").replace(/[^\w-]+/g, "_")}.pdf`;
  const blob = doc.output("blob");
  return { blob, filename };
}

/**
 * Delivers the built PDF to the user in the most useful way for their device:
 *  - Mobile with Web Share API + file support (iOS Safari, Chrome Android):
 *    opens the native share sheet so tradies can pick "Save to Files",
 *    AirDrop, Drive, Gmail, etc.
 *  - Otherwise: classic auto-download (desktop browsers, older mobiles).
 * Returns "shared" | "downloaded" so callers can toast an accurate message.
 */
export async function deliverJobPdf(job, user) {
  const { blob, filename } = await buildJobPdf(job, user);
  const file = new File([blob], filename, { type: "application/pdf" });
  // Only use share sheet when the browser confirms it can share this file.
  if (
    typeof navigator !== "undefined"
    && navigator.canShare
    && navigator.canShare({ files: [file] })
    && typeof navigator.share === "function"
  ) {
    try {
      await navigator.share({
        files: [file],
        title: job.name || "Site plan",
        text: `Field Plan report — ${job.name || "site"}`,
      });
      return "shared";
    } catch (err) {
      // User cancelled the share sheet — fall through to download.
      if (err && err.name === "AbortError") return "cancelled";
      // Any other error → fall through to download fallback.
    }
  }
  // Fallback: trigger a classic download.
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return "downloaded";
}
